import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProjectDetail } from '../ducks';
import { selectProjectById } from '../selectors';
import { Outline } from './Outline';

interface ProjectEditModalProps {
  projectId: string;
  onClose: () => void;
}

// Opens the FULL editable project outline in a dialog from the inbox Projects
// view's per-group Edit button (?projectEdit=<id>). It loads the single
// `detail` slice via fetchProjectDetail and reuses the existing <Outline> for
// zero editing-logic duplication (§D3). The inline inbox hierarchy reads the
// separate inboxOutlines slice, so it may be briefly stale behind the modal;
// the caller refetches outlines on close (§R6).
export function ProjectEditModal({ projectId, onClose }: ProjectEditModalProps) {
  const dispatch = useDispatch();
  const project = useSelector(selectProjectById(projectId));

  useEffect(() => {
    dispatch(fetchProjectDetail(projectId));
  }, [dispatch, projectId]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="todo-modal__backdrop" onClick={handleBackdropClick}>
      <div
        className="todo-modal project-edit-modal"
        role="dialog"
        aria-label={project ? `Edit project: ${project.title}` : 'Edit project'}
      >
        <button className="todo-modal__close" onClick={onClose} aria-label="Close">
          &times;
        </button>
        <header className="project-edit-modal__header">
          <h2>{project?.title ?? 'Project'}</h2>
          {project?.description && (
            <p className="project-edit-modal__desc">{project.description}</p>
          )}
        </header>
        <Outline projectId={projectId} />
      </div>
    </div>
  );
}

export default ProjectEditModal;
